import React, { useState } from 'react';
import { Plus, Trash2, Save, Wand2, Pencil, X } from 'lucide-react';
import { generateSkillsFromText, generateSkillsFromPosition, type Seniority } from '../../services/skillsGenerator';
import type { Skill } from './index';

interface InterviewFormProps {
  onSubmit: (
    position: string,
    seniority: Seniority,
    candidateName: string,
    interviewer: string,
    interviewDate: string,
    skills: Skill[]
  ) => void;
}

const seniorityOptions: { value: Seniority; label: string }[] = [
  { value: 'specialist', label: 'Especialista' },
  { value: 'senior', label: 'Sênior' },
  { value: 'pleno', label: 'Pleno' },
  { value: 'junior', label: 'Júnior' },
  { value: 'trainee', label: 'Trainee' },
];

export const InterviewForm: React.FC<InterviewFormProps> = ({ onSubmit }) => {
  const [position, setPosition] = useState('');
  const [seniority, setSeniority] = useState<Seniority>('pleno');
  const [candidateName, setCandidateName] = useState('');
  const [interviewer, setInterviewer] = useState('');
  const [interviewDate, setInterviewDate] = useState(new Date().toISOString().split('T')[0]);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [skillsText, setSkillsText] = useState('');
  const [isGeneratingSkills, setIsGeneratingSkills] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [newSkillName, setNewSkillName] = useState('');
  const [newSkillDescription, setNewSkillDescription] = useState('');
  const [editingSkill, setEditingSkill] = useState<string | null>(null);

  const handleGenerateFromPosition = async () => {
    if (!position || !seniority) {
      setGenerationError('Preencha o cargo e a senioridade para gerar as skills');
      return;
    }

    setIsGeneratingSkills(true);
    setGenerationError(null);

    try {
      const generatedSkills = await generateSkillsFromPosition(position, seniority);
      setSkills(prev => [...prev, ...generatedSkills]);
    } catch (error) {
      setGenerationError(error instanceof Error ? error.message : 'Erro ao gerar skills');
    } finally {
      setIsGeneratingSkills(false);
    }
  };

  const handleGenerateFromText = async () => {
    if (!skillsText.trim()) {
      setGenerationError('Digite o texto para gerar as skills');
      return;
    }

    setIsGeneratingSkills(true);
    setGenerationError(null);

    try {
      const generatedSkills = await generateSkillsFromText(skillsText);
      setSkills(prev => [...prev, ...generatedSkills]);
      setSkillsText('');
    } catch (error) {
      setGenerationError(error instanceof Error ? error.message : 'Erro ao gerar skills');
    } finally {
      setIsGeneratingSkills(false);
    }
  };

  const handleAddSkill = () => {
    if (!newSkillName.trim() || !newSkillDescription.trim()) return;

    const newSkill: Skill = {
      id: crypto.randomUUID(),
      name: newSkillName,
      description: newSkillDescription,
      evaluation: null
    };

    setSkills(prev => [...prev, newSkill]);
    setNewSkillName('');
    setNewSkillDescription('');
  };

  const handleRemoveSkill = (id: string) => {
    setSkills(prev => prev.filter(skill => skill.id !== id));
    if (editingSkill === id) {
      setEditingSkill(null);
    }
  };

  const handleEditSkill = (id: string) => {
    const skill = skills.find(s => s.id === id);
    if (skill) {
      setEditingSkill(id);
      setNewSkillName(skill.name);
      setNewSkillDescription(skill.description);
    }
  };

  const handleUpdateSkill = () => {
    if (!editingSkill || !newSkillName.trim() || !newSkillDescription.trim()) return;

    setSkills(prev => prev.map(skill => 
      skill.id === editingSkill
        ? { ...skill, name: newSkillName, description: newSkillDescription }
        : skill
    ));

    setEditingSkill(null);
    setNewSkillName('');
    setNewSkillDescription('');
  };

  const handleCancelEdit = () => {
    setEditingSkill(null);
    setNewSkillName('');
    setNewSkillDescription('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (position && seniority && candidateName && interviewer && interviewDate && skills.length > 0) {
      onSubmit(position, seniority, candidateName, interviewer, interviewDate, skills);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="position" className="block text-sm font-medium text-gray-700 mb-1">
            Cargo
          </label>
          <input
            type="text"
            id="position"
            value={position}
            onChange={(e) => setPosition(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="Ex: Desenvolvedor React"
            required
          />
        </div>

        <div>
          <label htmlFor="seniority" className="block text-sm font-medium text-gray-700 mb-1">
            Senioridade
          </label>
          <select
            id="seniority"
            value={seniority}
            onChange={(e) => setSeniority(e.target.value as Seniority)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            required
          >
            {seniorityOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="candidateName" className="block text-sm font-medium text-gray-700 mb-1">
            Nome do Candidato
          </label>
          <input
            type="text"
            id="candidateName"
            value={candidateName}
            onChange={(e) => setCandidateName(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="Nome completo do candidato"
            required
          />
        </div>

        <div>
          <label htmlFor="interviewer" className="block text-sm font-medium text-gray-700 mb-1">
            Nome do Entrevistador
          </label>
          <input
            type="text"
            id="interviewer"
            value={interviewer}
            onChange={(e) => setInterviewer(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="Seu nome completo"
            required
          />
        </div>

        <div>
          <label htmlFor="interviewDate" className="block text-sm font-medium text-gray-700 mb-1">
            Data da Entrevista
          </label>
          <input
            type="date"
            id="interviewDate"
            value={interviewDate}
            onChange={(e) => setInterviewDate(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Skills a serem avaliadas</h3>
        
        <div className="space-y-4 mb-6">
          {skills.map((skill) => (
            <div key={skill.id} className="bg-gray-50 p-4 rounded-lg">
              {editingSkill === skill.id ? (
                <div className="space-y-3">
                  <input
                    type="text"
                    value={newSkillName}
                    onChange={(e) => setNewSkillName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Nome da skill"
                  />
                  <textarea
                    value={newSkillDescription}
                    onChange={(e) => setNewSkillDescription(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                    placeholder="Descrição da skill"
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={handleUpdateSkill}
                      className="px-3 py-1.5 text-sm text-blue-600 hover:text-blue-800"
                    >
                      <Save className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="relative group">
                  <div className="absolute top-0 right-0 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      type="button"
                      onClick={() => handleEditSkill(skill.id)}
                      className="p-1 text-gray-400 hover:text-blue-600"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveSkill(skill.id)}
                      className="p-1 text-gray-400 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <h4 className="font-medium text-gray-900 mb-1">{skill.name}</h4>
                  <p className="text-sm text-gray-600">{skill.description}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="space-y-6">
          {/* Adicionar Skills Manualmente */}
          <div className="bg-gray-50 p-4 rounded-lg space-y-4">
            <h4 className="font-medium text-gray-900 flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Adicionar Skill Manualmente
            </h4>

            <div className="space-y-3">
              <div>
                <label htmlFor="skillName" className="block text-sm font-medium text-gray-700 mb-1">
                  Nome da Skill
                </label>
                <input
                  type="text"
                  id="skillName"
                  value={newSkillName}
                  onChange={(e) => setNewSkillName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ex: React Hooks"
                />
              </div>

              <div>
                <label htmlFor="skillDescription" className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição da Skill
                </label>
                <textarea
                  id="skillDescription"
                  value={newSkillDescription}
                  onChange={(e) => setNewSkillDescription(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Descreva o que deve ser avaliado nesta skill"
                  rows={3}
                />
              </div>

              <button
                type="button"
                onClick={handleAddSkill}
                disabled={!newSkillName.trim() || !newSkillDescription.trim()}
                className="w-full flex items-center justify-center gap-2 bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50"
              >
                <Plus className="w-4 h-4" />
                Adicionar Skill
              </button>
            </div>
          </div>

          {/* Gerar Skills com AI */}
          <div className="bg-blue-50 p-4 rounded-lg space-y-4">
            <h4 className="font-medium text-gray-900 flex items-center gap-2">
              <Wand2 className="w-4 h-4" />
              Gerar Skills com AI (Baseado no Cargo)
            </h4>

            <p className="text-sm text-gray-600">
              Gere automaticamente uma lista de skills relevantes com base no cargo e senioridade definidos.
              A IA irá considerar os requisitos técnicos, boas práticas e experiência necessária para a posição.
            </p>

            <button
              type="button"
              onClick={handleGenerateFromPosition}
              disabled={!position || !seniority || isGeneratingSkills}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:hover:bg-blue-600"
            >
              <Wand2 className={`w-4 h-4 ${isGeneratingSkills ? 'animate-spin' : ''}`} />
              {isGeneratingSkills ? 'Gerando Skills...' : 'Gerar Skills com AI'}
            </button>
          </div>

          {/* Gerar Skills com AI baseado em texto */}
          <div className="bg-purple-50 p-4 rounded-lg space-y-4">
            <h4 className="font-medium text-gray-900 flex items-center gap-2">
              <Wand2 className="w-4 h-4" />
              Gerar Skills com AI (Baseado em Texto)
            </h4>

            <p className="text-sm text-gray-600">
              Cole um texto descritivo da vaga ou requisitos técnicos para gerar automaticamente uma lista de skills relevantes.
            </p>

            <textarea
              value={skillsText}
              onChange={(e) => setSkillsText(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
              rows={4}
              placeholder="Cole aqui o texto com os requisitos da vaga..."
            />

            <button
              type="button"
              onClick={handleGenerateFromText}
              disabled={!skillsText.trim() || isGeneratingSkills}
              className="w-full flex items-center justify-center gap-2 bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:hover:bg-purple-600"
            >
              <Wand2 className={`w-4 h-4 ${isGeneratingSkills ? 'animate-spin' : ''}`} />
              {isGeneratingSkills ? 'Gerando Skills...' : 'Gerar Skills com AI'}
            </button>
          </div>

          {generationError && (
            <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
              {generationError}
            </div>
          )}
        </div>
      </div>

      <div className="pt-6 border-t">
        <button
          type="submit"
          disabled={!position || !seniority || !candidateName || !interviewer || !interviewDate || skills.length === 0}
          className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          Iniciar Avaliação
        </button>
      </div>
    </form>
  );
};